import {NotAuthenticated} from '@feathersjs/errors'
import {Params, feathers} from '@feathersjs/feathers'
import rest from '@feathersjs/rest-client'
import {merge} from 'es-toolkit'
import {batchClient} from 'feathers-batch'
import invariant from 'invariant'

import config from '../config'

import * as authService from '../../../commons/src/services/auth'

const feathersApp = feathers()

const restClient = rest(config.services.api.baseUrl)
feathersApp.configure(restClient.fetch(window.fetch.bind(window)))
feathersApp.hooks({
  before: {
    all: [
      async context => {
        const sessionToken = await authService.getCurrentSession()
        if (!sessionToken) {
          // When an error is thrown (or the promise is rejected), all subsequent hooks - and the service method call if it didn't run already - will be skipped.
          throw new NotAuthenticated(
            'No current session for feathers request - redirect to sign in'
          )
        }
        context.params = {
          ...context.params,
          ...{
            headers: {Authorization: `Bearer ${sessionToken}`},
          },
        }
        return context
      },
    ],
  },
})

// batchClient should be configured after any other application level hooks
feathersApp.configure(
  batchClient({
    batchService: 'batch',
  })
)

// GET LIST
export const find = async <T>(resource: string, params?: Params): Promise<T[]> => {
  const param = merge(
    {
      query: {
        $limit: -1, // get all entries (no pagination)
      },
    },
    params ?? {}
  )

  return feathersApp.service(resource).find(param)
}

// GET by ID
export const get = async <T>(resource: string, id: string, params?: Params): Promise<T> =>
  feathersApp.service(resource).get(id, params)

// CREATE
export const create = async <T, K>(resource: string, payload: K, params?: Params): Promise<T> =>
  feathersApp.service(resource).create(payload as Partial<K>, params)

export const createMultiple = async <T, K>(
  resource: string,
  payload: K[],
  params?: Params
): Promise<T[]> => feathersApp.service(resource).create(payload, params)

// UPDATE - Completely replace a single or multiple resources.
export const update = async <T>(
  resource: string,
  id: string,
  payload: T,
  params?: Params
): Promise<T> => {
  invariant(id, 'No id was provided for the update operation')
  return feathersApp.service(resource).update(id, payload as Partial<T>, params)
}

export const updateMultiple = async <T>(
  resource: string,
  payload: T,
  params?: Params
): Promise<T[]> => feathersApp.service(resource).update(null, payload as Partial<T>, params)

// PATCH - Merge the existing data of a single or multiple resources with the new data.
export const patch = async <T>(
  resource: string,
  id: string,
  payload: Partial<T>,
  params?: Params
): Promise<T> => {
  invariant(id, 'No id was provided for the patch operation')
  return feathersApp.service(resource).patch(id, payload, params)
}

export const patchMultiple = async <T>(
  resource: string,
  payload: Partial<T>,
  params?: Params
): Promise<T[]> => feathersApp.service(resource).patch(null, payload, params)

// DELETE
export const remove = async <T>(resource: string, id: string): Promise<T> => {
  invariant(id, 'No id was provided for the remove operation')
  return feathersApp.service(resource).remove(id)
}

export const removeMultiple = async <T>(resource: string, params?: Params): Promise<T[]> =>
  feathersApp.service(resource).remove(null, params)
